import * as DateF from 'date-fns'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, Typography } from '@blade/ui-kit'
import { useDateFnsLocale } from '@root/shared/localization'
import { Trash2Icon } from '@root/shared/ui-kit'

import { SessionDeviceIcon } from '../../common/session-device-icon'

import type { Session } from '@root/entities/auth'

type Props = {
  sessionInfo: Session.Session
  onDelete?: (sessionInfo: Session.Session) => void
}

export const SessionListItem: React.FC<Props> = ({ sessionInfo, onDelete }) => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()

  const renderLastSession = () => {
    if (sessionInfo.current) {
      return (
        <CurrentContainer>
          <CurrentIndicator />
          <span>{t('sessions.this_device')}</span>
        </CurrentContainer>
      )
    }

    const time = DateF.formatDistanceToNow(sessionInfo.lastLogin, { locale })
    const timeAgo = time ? t('common.ago', { time }) : t('common.n_a')

    return (
      <div
        css={css`
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
        `}
      >
        <div>
          <Label>{t('sessions.last_session')}</Label>
          <Value>{timeAgo}</Value>
        </div>
        <Button
          variant="ghost"
          size="xs"
          iconLeft={<Trash2Icon />}
          onClick={() => onDelete?.(sessionInfo)}
        />
      </div>
    )
  }

  return (
    <Root>
      <Device>
        <SessionDeviceIcon deviceType={sessionInfo.deviceType} />
        <div>
          <Label>{t('sessions.os')}</Label>
          <Value ellipsis>{sessionInfo.os ?? t('common.n_a')}</Value>
        </div>
      </Device>
      <div>
        <Label>{t('sessions.ip_address')}</Label>
        <Value ellipsis>{sessionInfo.ip}</Value>
      </div>
      {renderLastSession()}
    </Root>
  )
}

const Root = styled.div`
  border-radius: 12px;
  background: var(--background-2);

  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  height: 52px;
  gap: 14px;
  padding-block: 10px;
  padding-inline: 14px 10px;

  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const Label = styled.div`
  color: var(--text-9);

  font-weight: 600;
  font-size: 11px;
  margin-bottom: 2px;
`

const Device = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 14px;
`

const Value = styled(Typography)`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`

const CurrentIndicator = styled.div`
  width: 6px;
  height: 6px;
  background: var(--primary-9);
  border-radius: 50%;
`

const CurrentContainer = styled.div`
  color: var(--text-12);

  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 16px;

  display: grid;
  grid-template-columns: auto 1fr;
  justify-items: start;
  align-items: center;
`
